import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Zap, Eye, Flame } from "lucide-react";
import Logo from "./Logo";
import { cn } from "@/lib/utils";

// Desktop Trust Badges Component
const DesktopTrustBadges = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const badges = [
    { icon: Zap, text: "72h", color: "text-neon-cyan", tooltip: "Guaranteed 72-hour delivery" },
    { icon: Eye, text: "850K+", color: "text-white/70", tooltip: "Views generated for clients" },
    { icon: Flame, text: "97%", color: "text-neon-green", tooltip: "97% of clients reorder" },
  ];

  return (
    <div className={cn(
      "hidden lg:flex items-center gap-1 ml-4 transition-all duration-700",
      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
    )}>
      {badges.map((badge, index) => (
        <div
          key={badge.text}
          className="group relative flex items-center gap-1 px-2 py-1 rounded-full 
                     bg-white/5 border border-white/10 hover:border-neon-purple/40
                     transition-all duration-300 hover:bg-white/10 cursor-default desktop-badge-hover"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <badge.icon className={cn("w-3 h-3", badge.color)} />
          <span className={cn("text-[10px] font-bold", badge.color)}>{badge.text}</span>
          
          {/* Tooltip */}
          <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 px-2 py-1 
                          bg-background/95 border border-white/20 rounded text-[10px] text-white/80
                          opacity-0 group-hover:opacity-100 scale-95 group-hover:scale-100
                          transition-all duration-200 whitespace-nowrap pointer-events-none z-50
                          shadow-lg shadow-black/50">
            {badge.tooltip}
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-background/95 
                            border-l border-t border-white/20 rotate-45" />
          </div>
          
          {/* Neon underline on hover */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[1px] 
                          bg-gradient-to-r from-neon-purple to-neon-pink
                          group-hover:w-3/4 transition-all duration-300" />
        </div>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const scrollingDown = scrollPosition > lastScrollY.current;
      const scrollDelta = Math.abs(scrollPosition - lastScrollY.current);
      
      if (scrollDelta > 5) {
        setIsHidden(scrollingDown && scrollPosition > 50);
      }
      
      lastScrollY.current = scrollPosition;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 glass-morphism border-b border-border/50 transition-transform duration-300 ${
        isHidden ? '-translate-y-full' : 'translate-y-0'
      }`} style={{ height: '70px' }}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo + Desktop Trust Badges */}
            <div className="flex items-center">
              <a href="/" className="block tap-feedback magnetic-hover" aria-label="Fuel72 Home">
                <Logo className="h-7 md:h-8 lg:h-9 w-auto" variant="horizontal" />
              </a>
              <DesktopTrustBadges />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <button
                onClick={() => scrollToSection("hero")}
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                🚀 Launch Pad
              </button>
              <button
                onClick={() => scrollToSection("how-it-works")}
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                Fuel Packs
              </button>
              <button
                onClick={() => scrollToSection("social-proof")}
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                Results
              </button>
              <button
                onClick={() => scrollToSection("faq")}
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                FAQ
              </button>
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center">
              <Button
                onClick={() => window.location.href = "/order"}
                className="bg-gradient-to-r from-orange-500 via-neon-pink to-orange-600 hover:opacity-90 text-white rounded-full font-heading font-semibold text-sm lg:text-base px-6 lg:px-7 h-10 lg:h-11 animate-glow-pulse hover:animate-flame-flicker btn-premium"
              >
                <span className="mr-1">⛽</span> Fuel My Business
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors tap-feedback"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          <div 
            className={`md:hidden absolute top-full left-0 right-0 pb-4 pt-2 space-y-1 bg-background/95 backdrop-blur-xl border-b border-border/50 px-4 transition-all duration-300 ease-out ${
              isMenuOpen 
                ? 'opacity-100 translate-y-0 pointer-events-auto' 
                : 'opacity-0 -translate-y-2 pointer-events-none'
            }`}
          >
              <button
                onClick={() => scrollToSection("hero")}
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                🚀 Launch Pad
              </button>
              <button
                onClick={() => scrollToSection("how-it-works")}
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                ⛽ Fuel Packs
              </button>
              <button
                onClick={() => scrollToSection("social-proof")}
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                Results
              </button>
              <button
                onClick={() => scrollToSection("faq")}
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                FAQ
              </button>
              
              <div className="px-4 pt-2">
                <Button
                  onClick={() => window.location.href = "/order"}
                  className="w-full bg-gradient-to-r from-orange-500 via-neon-pink to-orange-600 hover:opacity-90 text-white rounded-full font-heading font-semibold h-12 text-base animate-glow-pulse tap-feedback"
                >
                  <span className="mr-1">⛽</span> Fuel My Business
                </Button>
              </div>
          </div>
        </div>
      </nav>

      {/* Mobile Trust Anchors Bar - Always visible below navbar */}
      <div 
        className={`md:hidden fixed left-0 right-0 z-40 transition-all duration-300 ${
          isHidden ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100'
        }`}
        style={{ top: '70px' }}
      >
        <div className="bg-background/60 backdrop-blur-md border-b border-white/5 py-1.5">
          <div className="flex items-center justify-center gap-2 text-[10px] animate-trust-bar-entrance">
            <span className="text-neon-cyan font-medium flex items-center gap-1">
              <span className="animate-pulse">⚡</span> 72h
            </span>
            <span className="text-white/30">•</span>
            <span className="text-white/60 flex items-center gap-1">
              👁️ 850K+
            </span>
            <span className="text-white/30">•</span>
            <span className="text-neon-green font-medium flex items-center gap-1">
              🔥 97%
            </span>
            <span className="text-white/30">•</span>
            <span className="text-white/60 flex items-center gap-1">
              🚀 250+
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
